var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useRef, useMemo, useImperativeHandle, memo } from 'react';
import DynamicForm from '../DynamicForm/DynamicForm';
import { DataEntrySection } from './DataEntrySection';
import { DataEntryField } from './DataEntryField';
import { DataEntrySubSection } from './DataEntrySubSection';
const DataEntryFormBase = React.forwardRef((props, ref) => {
    const formRef = useRef(null);
    const { item, children } = props, dynamicFormProps = __rest(props, ["item", "children"]);
    // Expose DynamicForm handlers to parent
    useImperativeHandle(ref, () => ({
        submit: () => __awaiter(void 0, void 0, void 0, function* () {
            if (formRef.current) {
                yield formRef.current.submit();
            }
        }),
        cancel: () => {
            var _a;
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.cancel();
        },
        validate: () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            return ((_a = formRef.current) === null || _a === void 0 ? void 0 : _a.validate()) || false;
        }),
        getFormData: () => {
            var _a;
            return ((_a = formRef.current) === null || _a === void 0 ? void 0 : _a.getFormData()) || {};
        },
        setFormData: (data) => {
            var _a;
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.setFormData(data);
        },
        getErrors: () => {
            var _a;
            return ((_a = formRef.current) === null || _a === void 0 ? void 0 : _a.getErrors()) || {};
        },
        clearErrors: () => {
            var _a;
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.clearErrors();
        },
        setErrors: (errors) => {
            var _a;
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.setErrors(errors);
        },
        clearFieldError: (fieldName) => {
            var _a;
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.clearFieldError(fieldName);
        },
        isProcessing: () => {
            var _a;
            return ((_a = formRef.current) === null || _a === void 0 ? void 0 : _a.isProcessing()) || false;
        },
        goToNext: () => {
            var _a;
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.goToNext();
        },
        goToPrevious: () => {
            var _a;
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.goToPrevious();
        },
        getWizardState: () => {
            var _a;
            return ((_a = formRef.current) === null || _a === void 0 ? void 0 : _a.getWizardState()) || null;
        },
    }));
    // Extract and transform form structure from children
    const formStructure = useMemo(() => {
        const sections = [];
        React.Children.forEach(children, (child) => {
            if (React.isValidElement(child) && child.type === DataEntrySection) {
                const sectionProps = child.props;
                const mapField = (fieldProps) => ({
                    name: fieldProps.field,
                    label: fieldProps.title,
                    type: fieldProps.type || 'text',
                    value: fieldProps.value !== undefined
                        ? fieldProps.value
                        : item
                            ? item[fieldProps.field]
                            : undefined,
                    placeholder: fieldProps.placeholder,
                    icon: fieldProps.icon,
                    renderField: fieldProps.children,
                    show: fieldProps.show,
                    options: fieldProps.options,
                    getOptions: fieldProps.getOptions,
                    getPaginatedOptions: fieldProps.getPaginatedOptions,
                    selectedOptionLabel: fieldProps.selectedOptionLabel,
                    labelField: fieldProps.labelField,
                    valueField: fieldProps.valueField,
                    allowZero: fieldProps.allowZero,
                    allowNegative: fieldProps.allowNegative,
                    formatter: fieldProps.formatter,
                    validate: {
                        required: fieldProps.required,
                        allowEmptyString: fieldProps.allowEmptyString,
                        minLength: fieldProps.minLength,
                        maxLength: fieldProps.maxLength,
                        regExp: fieldProps.regExp,
                        allowZero: fieldProps.allowZero,
                        allowNegative: fieldProps.allowNegative,
                        minVal: fieldProps.minVal,
                        maxVal: fieldProps.maxVal,
                        customValidateFunction: fieldProps.validate,
                    },
                });
                // Check if section children are DataEntrySubSection or flat DataEntryField
                const hasSubSections = React.Children.toArray(sectionProps.children)
                    .some(c => React.isValidElement(c) && c.type === DataEntrySubSection);
                if (hasSubSections) {
                    // Map sub-sections
                    const subSections = [];
                    React.Children.forEach(sectionProps.children, (subChild) => {
                        if (React.isValidElement(subChild) && subChild.type === DataEntrySubSection) {
                            const subProps = subChild.props;
                            const subFields = [];
                            React.Children.forEach(subProps.children, (fieldChild) => {
                                if (React.isValidElement(fieldChild) && fieldChild.type === DataEntryField) {
                                    subFields.push(mapField(fieldChild.props));
                                }
                            });
                            subSections.push({
                                title: subProps.title,
                                columns: subProps.columns || 1,
                                maxColumnWidth: subProps.maxColumnWidth,
                                seperator: subProps.separator,
                                show: subProps.show,
                                collapsible: subProps.collapsible,
                                defaultExpanded: subProps.defaultExpanded,
                                fields: subFields,
                            });
                        }
                    });
                    sections.push({
                        title: sectionProps.title,
                        show: sectionProps.show,
                        collapsible: sectionProps.collapsible,
                        defaultExpanded: sectionProps.defaultExpanded,
                        fields: [],
                        sections: subSections,
                    });
                }
                else {
                    // Flat fields (existing behaviour)
                    const fields = [];
                    React.Children.forEach(sectionProps.children, (fieldChild) => {
                        if (React.isValidElement(fieldChild) && fieldChild.type === DataEntryField) {
                            fields.push(mapField(fieldChild.props));
                        }
                    });
                    sections.push({
                        title: sectionProps.title,
                        columns: sectionProps.columns || 1,
                        maxColumnWidth: sectionProps.maxColumnWidth,
                        seperator: sectionProps.separator,
                        show: sectionProps.show,
                        collapsible: sectionProps.collapsible,
                        defaultExpanded: sectionProps.defaultExpanded,
                        fields,
                    });
                }
            }
            else {
                // sections.push({
                //     title:'',
                //     columns: 1,
                //     seperator: false,
                //     collapsible: false,
                //     defaultExpanded: true,
                //     fields:[{
                //         type:'readonly',
                //         renderField(data, onValueChange) {
                //             return child;
                //         },
                //         name:'',
                //         label:'',
                //     } ]
                // })
            }
        });
        // Debug logging - log the generated form structure
        console.group('📋 DataEntryForm → DynamicForm', sections);
        console.log('Sections:', sections.length);
        sections.forEach((section, idx) => {
            console.group(`Section ${idx + 1}: ${section.title || '(untitled)'}`);
            console.log('Columns:', section.columns);
            console.log('Fields:', section.fields.length);
            section.fields.forEach((field) => {
                console.log(`  - ${field.name} (${field.type}):`, field.value);
            });
            console.groupEnd();
        });
        console.log('Full structure:', sections);
        console.groupEnd();
        return sections;
    }, [children, item]);
    return (_jsx(DynamicForm, Object.assign({}, dynamicFormProps, { formStructure: formStructure, ref: formRef })));
});
/**
 * @export
 * DataEntryForm component - A declarative JSX wrapper around DynamicForm
 *
 * Provides a simple JSX-based API for creating forms while supporting all DynamicForm features.
 * This is a pure form component without any panel/modal integration.
 *
 * @example
 * ```tsx
 * const formRef = useRef<DataEntryFormHandlers>(null);
 *
 * <DataEntryForm
 *   item={user}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 *   renderStyle="tabs"
 *   ref={formRef}
 * >
 *   <DataEntrySection title="Basic Info" columns={2}>
 *     <DataEntryField field="firstName" title="First Name" required />
 *     <DataEntryField field="lastName" title="Last Name" required />
 *     <DataEntryField
 *       field="email"
 *       title="Email"
 *       type="email"
 *       icon="fas at"
 *       required
 *       validate={async (val) => {
 *         const valid = val.includes('@');
 *         return { valid, error: valid ? undefined : 'Invalid email' };
 *       }}
 *     />
 *   </DataEntrySection>
 * </DataEntryForm>
 * ```
 */
export const DataEntryForm = memo(DataEntryFormBase);
